import { useEffect, useState } from 'react';
import useCookieManager from './useCookieManager';

export interface Cookie {
  name: string;
  value: string;
}

export interface CookieChangedEvent extends Event {
  changed: Readonly<Array<Cookie>>;
  deleted: Readonly<Array<Cookie>>;
}

export interface CookieStore {
  addEventListener(eventName: 'change', handler: (event: CookieChangedEvent) => void): void;
  removeEventListener(eventName: 'change', handler: (event: CookieChangedEvent) => void): void;
  onchange: null | ((event: CookieChangedEvent) => void);
}

const useCookieListener = (
  cookieName: string
): {
  cookie: Cookie | null;
} => {
  const { getCookie } = useCookieManager();
  const [cookie, setCookie] = useState<Cookie | null>(null);

  const handleCookieEvent = (event: CookieChangedEvent) => {
    const foundCookie = event.changed.find((cookie) => cookie.name === cookieName);
    if (foundCookie) {
      setCookie(foundCookie);
    }
  };

  useEffect(() => {
    if (!window?.cookieStore) {
      return;
    }

    if (!cookie) {
      const value = getCookie(cookieName);
      if (value) {
        setCookie({ name: cookieName, value });
      }
    }

    const cookieStore = window.cookieStore as CookieStore;
    cookieStore.addEventListener('change', handleCookieEvent);
    return () => cookieStore.removeEventListener('change', handleCookieEvent);
  }, [cookieName]);

  return {
    cookie,
  };
};

export default useCookieListener;
